import {
  faCheckCircle,
  faCheckSquare,
  faCircle,
  faDotCircle,
  faSquare,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons'
import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome'
import React, { forwardRef, RefObject, useRef } from 'react'

import useOption from './useOption'
import { twMerge } from 'tailwind-merge'
import { Button } from '../../../../Button'
import classNames from 'classnames'
import { CheckboxOrRadio } from './CheckboxOrRadio'

export interface SwitchOptionVariantProps {
  label: string
  onClick: (
    e: React.MouseEvent<HTMLLabelElement | HTMLButtonElement, MouseEvent>,
  ) => void
  disabled?: boolean
  selected?: boolean
  role?: 'switch' | 'checkbox' | 'radio'
  children: React.ReactNode
}

export const SwitchOptionVariant = forwardRef<
  HTMLLabelElement | HTMLButtonElement,
  SwitchOptionVariantProps
>((props, ref) => {
  const optionGroupButtonClasses = twMerge(
    'relative flex flex-col transition gap-2 ease-in-out cursor-pointer rounded-lg border',
    'p-4 shadow-sm focus:outline-none border-gray-300 bg-white dark:bg-transparent',
    classNames({
      'ringe-2 border-2 ring-indigo-600 border-indigo-600': props.selected,
      'ring-gray-200 border-gray-200': props.selected && props.disabled,
      'bg-base-200 cursor-not-allowed': props.disabled,
    }),
  )

  const forwardedRef = ref as RefObject<HTMLLabelElement>

  return (
    <label
      ref={forwardedRef}
      onFocus={() => forwardedRef?.current?.focus()}
      tabIndex={0}
      aria-label={props.label}
      onClick={props.onClick}
      aria-disabled={props.disabled}
      aria-checked={props.selected}
      className={optionGroupButtonClasses}>
      <div className='flex items-center'>
        <span className='block text-sm font-medium text-base-900 grow'>
          {props.label}
        </span>

        {props.role === 'switch' && (
          <button
            type='button'
            disabled={props.disabled}
            onClick={props.onClick}
            className={twMerge(
              ' ml-4 relative inline-flex shrink-0 h-6 w-11 border-2 border-transparent',
              'rounded-full cursor-pointer transition-colors ease-in-out duration-200',
              'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500',
              props.selected ? 'bg-indigo-600' : 'bg-base-200',
            )}
            role='switch'
            aria-checked={props.selected}>
            <span
              aria-hidden='true'
              className={twMerge(
                'inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                props.selected ? 'translate-x-5' : 'translate-x-0',
              )}></span>
          </button>
        )}

        {props.role !== 'switch' && (
          <CheckboxOrRadio role={props.role} selected={props.selected} />
        )}
      </div>

      <span
        onClick={props.onClick}
        className={twMerge(
          'mt-1 flex items-center text-sm text-base-500',
          !props.children && 'hidden',
        )}>
        {props.children}
      </span>
    </label>
  )
})
